import { Modal } from "@arco-design/web-react";
import { useEffect, useState } from "react";
import { Area } from "react-easy-crop/types";

import { getCroppedImg, base64ToFile, fileToBase64 } from './helpers';
import { ImageCropperPane } from '../ImageCrop/ImageCropperPane';
import './AvatarCropDialog.css';

type Props = {
  visible: boolean;
  file: File;
  onCancel: () => void;
  onConfirm: (file: File) => void;
  /** Square cards/profile heroes need a rect crop; round is legacy circle avatars. */
  cropShape?: "round" | "rect";
};

export function AvatarCropDialog({
  visible,
  file,
  onConfirm,
  onCancel,
  cropShape = "rect",
}: Props) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [rotation, setRotation] = useState(0);

  const onCropSubmit = async () => {
    if (!croppedAreaPixels || !imageSrc) {
      return;
    }

    try {
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation
      );
      if (!croppedImage) {
        return;
      }
      onConfirm(base64ToFile(croppedImage, file.name, file.type));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!visible) {
      return;
    }
    const timer = setTimeout(() => {
      fileToBase64(file).then(async (imageDataUrl) => {
        if (typeof imageDataUrl !== 'string') {
          return;
        }
        setImageSrc(imageDataUrl);
      }).catch(() => {
        console.warn('failed to detect the orientation');
      });
    }, 200);

    return () => clearTimeout(timer);
  }, [file, visible]);

  useEffect(() => {
    if (!visible) {
      setImageSrc(null);
      setCroppedAreaPixels(null);
      setRotation(0);
    }
  }, [visible]);

  return (
    <Modal
      className="avatar-crop-modal"
      visible={visible}
      okText="Crop"
      onCancel={onCancel}
      onConfirm={onCropSubmit}
      alignCenter
      getPopupContainer={() => document.body}
      unmountOnExit
    >
      {visible && imageSrc && (
        <ImageCropperPane
          imageSrc={imageSrc}
          aspect={1}
          cropShape={cropShape}
          variant="default"
          onCropComplete={() => undefined}
          onCropAreaChange={(pixels, nextRotation) => {
            setCroppedAreaPixels(pixels);
            setRotation(nextRotation);
          }}
        />
      )}
    </Modal>
  );
}
